import { ReconciliationSupplierDetail, useUploadMonthlyInvoiceApi } from 'apis/reconciliation';
import type { UploadedFile } from 'design-system';
import {
  Box,
  Button,
  FileUploader,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useCustomToast,
} from 'design-system';
import { formatAed } from 'pages/reconciliation/utils/format';
import { INVOICE_PDF_ACCEPT_TYPES, MAX_INVOICE_FILE_SIZE } from 'pages/reconciliation/utils/invoice-files';
import { isMonthlyInvoiceNumber } from 'pages/reconciliation/utils/invoices';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

type TInputs = {
  invoiceNumber: string;
  netAmount: string;
};

const defaultValues: TInputs = { invoiceNumber: '', netAmount: '' };

export interface IUploadMonthlyInvoiceModal {
  month: string;
  detail: ReconciliationSupplierDetail;
  isOpen: boolean;
  onClose: () => void;
}

/** Registers a Finance-issued monthly invoice PDF instead of generating one.
 *
 * A file input alone would not do: the number is typed by hand (it pushes the BE's monthly
 * counter forward so generation can never reuse it) and the amounts cannot be read out of a
 * PDF, so ops supplies the net fee and the BE derives VAT and total at the month's live rate.
 * The preview below the field shows exactly what will be stored, so the ledger figure is never
 * a surprise after the fact. */
export const UploadMonthlyInvoiceModal = ({ month, detail, isOpen, onClose }: IUploadMonthlyInvoiceModal) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<TInputs>({ defaultValues });

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      setFile(null);
    }
  }, [isOpen, reset]);

  const { mutate: uploadInvoice, isLoading } = useUploadMonthlyInvoiceApi({
    onSuccess: ({ invoice }) => {
      toast({ description: `Monthly invoice ${invoice.invoiceNumber} uploaded`, status: 'success' });
      queryClient.invalidateQueries(['reconciliation-supplier-details', month, detail.supplierId]);
      queryClient.invalidateQueries(['reconciliation-supplier-orders', month, detail.supplierId]);
      queryClient.invalidateQueries(['reconciliation-suppliers']);
      queryClient.invalidateQueries(['reconciliation-summary', month]);
      onClose();
    },
  });

  const vatRate = detail.vatRate ?? 0;
  const netAmount = Number(watch('netAmount'));
  const hasPreview = Number.isFinite(netAmount) && netAmount > 0;
  const vatAmount = hasPreview ? Math.round(netAmount * vatRate) / 100 : 0;

  const onSubmit = (values: TInputs) => {
    if (!file) return;
    uploadInvoice({
      month,
      supplierId: detail.supplierId,
      invoiceNumber: values.invoiceNumber.trim(),
      netAmount: Number(values.netAmount),
      file,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Upload monthly invoice</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stack spacing='md'>
            <FormControl isInvalid={!!errors.invoiceNumber}>
              <FormLabel>Invoice number</FormLabel>
              <Input
                placeholder='EK-0826-007'
                {...register('invoiceNumber', {
                  required: 'Enter the invoice number printed on the PDF',
                  validate: (value) =>
                    isMonthlyInvoiceNumber(value) || 'Use the monthly format EK-MMYY-NNN, e.g. EK-0826-007',
                })}
              />
              {errors.invoiceNumber ? (
                <FormErrorMessage>{errors.invoiceNumber.message}</FormErrorMessage>
              ) : (
                <FormHelperText>
                  Generated invoices continue from this number, so it can never be reused.
                </FormHelperText>
              )}
            </FormControl>

            <FormControl isInvalid={!!errors.netAmount}>
              <FormLabel>Net amount (AED)</FormLabel>
              <Input
                type='number'
                step='0.01'
                placeholder='2730.00'
                {...register('netAmount', {
                  required: 'Enter the fee before tax',
                  validate: (value) => Number(value) > 0 || 'Enter an amount greater than zero',
                })}
              />
              {errors.netAmount ? (
                <FormErrorMessage>{errors.netAmount.message}</FormErrorMessage>
              ) : (
                <FormHelperText>
                  The fee before tax. VAT and total are calculated at this month&apos;s {vatRate}% rate.
                </FormHelperText>
              )}
            </FormControl>

            {hasPreview && (
              <Box bg='gray.50' borderRadius='md' p='sm'>
                <Stack spacing='2px'>
                  <PreviewRow label='Net' value={netAmount} />
                  <PreviewRow label={`VAT (${vatRate}%)`} value={vatAmount} />
                  <PreviewRow label='Total' value={Math.round((netAmount + vatAmount) * 100) / 100} isBold />
                </Stack>
              </Box>
            )}

            <FormControl isInvalid={!file}>
              <FormLabel>Invoice PDF</FormLabel>
              <FileUploader
                style='style_01'
                message='Drop the invoice PDF here or browse'
                acceptedTypes={INVOICE_PDF_ACCEPT_TYPES}
                maxFiles={1}
                // Explicit — FileUploader defaults to 5MB, below the BE's 10MB limit.
                maxFileSize={MAX_INVOICE_FILE_SIZE}
                onRemove={() => setFile(null)}
                onRemoveAll={() => setFile(null)}
                // The component may hand back the whole selection or only the new file;
                // maxFiles={1} means the last one is always the one ops meant.
                onUpload={(uploaded: UploadedFile[]) => setFile(uploaded[uploaded.length - 1] ?? null)}
              />
              {file && (
                <Text fontSize='sm' color='gray.600' mt='xs' isTruncated>
                  {file.name}
                </Text>
              )}
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing='sm'>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' colorScheme='red' isLoading={isLoading} isDisabled={!file}>
              Upload
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const PreviewRow = ({ label, value, isBold }: { label: string; value: number; isBold?: boolean }) => (
  <HStack justify='space-between'>
    <Text fontSize='sm' color={isBold ? 'gray.700' : 'gray.500'} fontWeight={isBold ? 'bold' : 'normal'}>
      {label}
    </Text>
    <Text fontSize='sm' fontWeight={isBold ? 'bold' : 'medium'} color='gray.700' whiteSpace='nowrap'>
      {formatAed(value)}
    </Text>
  </HStack>
);
